/**
 * The platformer player — split out of the old PlatformerEntities.js
 * monolith (see PlatformerEntities.js, which barrel-re-exports this).
 *
 * Mario-style player. Tracks "small" / "super" power state and
 * handles input every frame: walk speed (with run modifier),
 * variable-height jump, coyote time, and post-hit invincibility.
 *
 * The visible sprite is independent of the collision box so growing
 * to "super" simply scales the sprite — the box stays a tight 48 ×
 * h rectangle that fits through 1-tile gaps (since no v1 stage has
 * 1-tile gaps for super, growing tall is purely cosmetic + a hit
 * point).
 */

import { Container, Graphics } from '../../../vendor/pixi.min.mjs';
import {
    PLATFORMER_PHYSICS,
    PLATFORMER_PLAYER,
    PLATFORMER_ENTITY_DEFS,
} from './platformerConfig.js';
import {
    stepBody,
    resolveJumpInput,
    groundMaterialFriction,
} from './PlatformerPhysics.js';
import { spawnSprite } from './entityHelpers.js';

export class PlayerEntity {
    constructor(c, r, board, theme, opts = {}) {
        this._theme = theme;
        this._board = board;
        const cs = board.cellSize;
        const w = PLATFORMER_PLAYER.width;
        const h = PLATFORMER_PLAYER.height;

        // Position: bottom-aligned at the spawn cell so the player
        // stands on the tile beneath their feet (cell (c, r+1)).
        this.x = c * cs + (cs - w) / 2;
        this.y = (r + 1) * cs - h;
        this.w = w;
        this.h = h;
        this.vx = 0;
        this.vy = 0;
        this.facing = 1;
        this.onGround = false;

        this.coyoteTimer = 999;
        this.jumpBufferTimer = 0;
        this.jumpHeld = false;
        this._jumpedThisCoyote = false;

        // Per-player physics — every value resolves through a 3-tier
        // precedence so a level designer can fine-tune one specific
        // player spawn without touching the rest of the game:
        //
        //   1. the spawn entity's own `props.<key>`  (set in the editor)
        //   2. the global OptionsScene setting        (jumpStrength only)
        //   3. the shared PLATFORMER_PHYSICS default   (everything else)
        //
        // stepBody() + resolveJumpInput() read `body.physics` when it's
        // present and fall back to the shared constant otherwise, so
        // enemies (which never set `.physics`) keep the global tuning.
        this.physics = this._resolvePhysics(theme, opts.props || {});
        // Back-compat alias still used by a couple of call sites.
        this.jumpVelocity = this.physics.jumpVelocity;

        // Power state — 'small' (default) or 'super'. 'super' grows
        // the visual + grants one free hit before death.
        this.power = opts.power || 'small';
        this.invincibleTimer = 0;
        this.alive = true;
        this.won = false;

        this.display = new Container();
        this._buildVisual();
    }

    /**
     * Build this player's effective physics object, layering the spawn
     * entity's props on top of the global setting / config defaults.
     * `props` is the `player.spawn` entity's `props` map from the stage
     * JSON (e.g. { jumpStrength: 18, gravity: 0.5, friction: 0.05 }).
     */
    _resolvePhysics(theme, props) {
        const base = PLATFORMER_PHYSICS;
        const num = (v, fb) => (Number.isFinite(v) ? v : fb);
        // jumpStrength is the one tunable that also has a global
        // OptionsScene slider, so it gets the extra middle tier.
        const settingJump = theme?.getSetting?.(
            'platformer', 'jumpStrength', Math.abs(base.jumpVelocity),
        ) ?? Math.abs(base.jumpVelocity);
        const jumpStrength = num(props.jumpStrength, settingJump);

        return {
            gravity:             num(props.gravity, base.gravity),
            fallGravityMult:     num(props.fallGravityMult, base.fallGravityMult),
            terminalVelocity:    num(props.terminalVelocity, base.terminalVelocity),
            walkSpeed:           num(props.walkSpeed, base.walkSpeed),
            runSpeed:            num(props.runSpeed, base.runSpeed),
            friction:            num(props.friction, base.friction),
            airControl:          num(props.airControl, base.airControl),
            // Stored negative (upward) so resolveJumpInput uses it raw.
            jumpVelocity:        -Math.abs(jumpStrength),
            jumpReleaseVelocity: num(props.jumpReleaseVelocity, base.jumpReleaseVelocity),
            coyoteFrames:        num(props.coyoteFrames, base.coyoteFrames),
            jumpBufferFrames:    num(props.jumpBufferFrames, base.jumpBufferFrames),
            stompBounce:         base.stompBounce,
        };
    }

    _buildVisual() {
        this.display.removeChildren();
        const def = PLATFORMER_ENTITY_DEFS['player.spawn'];
        const drawW = this.w;
        const drawH = this.h * (this.power === 'super' ? PLATFORMER_PLAYER.superHeightMultiplier : 1.0);
        const sprite = spawnSprite(this._theme, def.slot, drawW, drawH);
        if (sprite) {
            sprite.anchor?.set?.(0, 1);
            sprite.x = 0;
            sprite.y = this.h;
            this.display.addChild(sprite);
            this._sprite = sprite;
            return;
        }
        // Procedural fallback — chunky 2-tone humanoid silhouette.
        const g = new Graphics();
        const w = drawW;
        const h = drawH;
        g.rect(w * 0.15, h * 0.30, w * 0.70, h * 0.45);
        g.fill(0xef4444);                             // body
        g.rect(w * 0.20, h * 0.05, w * 0.60, h * 0.30);
        g.fill(0xfde68a);                             // head
        g.rect(w * 0.20, h * 0.70, w * 0.25, h * 0.30);
        g.rect(w * 0.55, h * 0.70, w * 0.25, h * 0.30);
        g.fill(0x1f2937);                             // legs
        g.rect(w * 0.30, h * 0.15, w * 0.10, h * 0.06);
        g.rect(w * 0.60, h * 0.15, w * 0.10, h * 0.06);
        g.fill(0x111827);                             // eyes
        g.x = 0;
        g.y = this.h - h;       // bottom-anchor inside the display
        this.display.addChild(g);
        this._sprite = g;
    }

    update(controls, board) {
        if (!this.alive || this.won) return;

        // Horizontal input → desired velocity. Air control trims the
        // turning authority while jumping so the player can't fully
        // reverse mid-air (Mario-feel: 85 % of ground accel).
        const T = this.physics;
        const targetSpeed = controls.run ? T.runSpeed : T.walkSpeed;
        const accel = (this.onGround ? 1.0 : T.airControl);
        if (controls.left) {
            this.vx -= 0.6 * accel;
            if (this.vx < -targetSpeed) this.vx = -targetSpeed;
            this.facing = -1;
        } else if (controls.right) {
            this.vx += 0.6 * accel;
            if (this.vx > targetSpeed) this.vx = targetSpeed;
            this.facing = 1;
        } else if (this.onGround) {
            // Friction on the ground only — air keeps inertia so
            // jumps off ledges land naturally. The material under the
            // feet can override the player's base friction (ice = low
            // → keep sliding; sand = high → stop fast); falls back to
            // the player's own friction over plain ground.
            const f = groundMaterialFriction(this, board) ?? T.friction;
            if (this.vx > 0) this.vx = Math.max(0, this.vx - f);
            else if (this.vx < 0) this.vx = Math.min(0, this.vx + f);
        }

        this.jumpHeld = !!controls.jumpHeld;
        // Juice hooks: expose "jumped this frame" and "landed this
        // frame (at what speed)" so the scene can fire SFX, dust and
        // squash & stretch without re-deriving physics state.
        this.justJumped = resolveJumpInput(this, controls);
        const fallVy = this.vy;             // downward speed entering the step

        const summary = stepBody(this, board);
        this.justLanded = (summary.landedOnGround && fallVy > 1) ? fallVy : 0;

        // Brick / ?-block bonk — a head-bonk against a `bumpable` cell
        // emits a "bumped" event so the scene can replace ?-blocks
        // with hard blocks and break bricks.
        if (summary.bumpedTile && board.bumpableAt(summary.bumpedTile.c, summary.bumpedTile.r)) {
            this._lastBump = summary.bumpedTile;
        } else {
            this._lastBump = null;
        }

        if (this.invincibleTimer > 0) {
            this.invincibleTimer -= 1;
            // Flicker the sprite while invincible.
            this.display.alpha = (this.invincibleTimer % 8 < 4) ? 0.4 : 1.0;
        } else {
            this.display.alpha = 1.0;
        }

        this._syncDisplay();
    }

    _syncDisplay() {
        this.display.x = this.x;
        this.display.y = this.y;
        if (this._sprite) {
            this._sprite.scale.x = Math.abs(this._sprite.scale?.x || 1) * (this.facing >= 0 ? 1 : -1);
            // Keep facing simple: flip horizontally by mirroring scale.
            // (Re-anchor so the flipped sprite still occupies the same
            // bounds as the unflipped one.)
            this._sprite.scale.x = (this.facing >= 0 ? 1 : -1) * Math.abs(this._sprite.scale.x);
            this._sprite.x = (this.facing >= 0 ? 0 : this.w);
        }
    }

    /** Take a hit. Super → small (with brief invincibility). Small → die. */
    takeDamage() {
        if (this.invincibleTimer > 0) return false;
        if (this.power === 'super') {
            this.power = 'small';
            this.invincibleTimer = PLATFORMER_PLAYER.hitInvincibleFrames;
            this._buildVisual();
            return false;
        }
        this.alive = false;
        return true;
    }

    /** Mushroom pickup — small → super. No-op when already super. */
    powerUp() {
        if (this.power === 'super') return;
        this.power = 'super';
        this._buildVisual();
    }

    /** The pit kills regardless of power state. */
    fellInPit() {
        this.alive = false;
    }

    /** Bounce off a stomped enemy (Mario classic ~half-jump pop). */
    stompBounce() {
        this.vy = this.physics.stompBounce;
        this.onGround = false;
    }

    /** Player pos → top-left of cell — handy for camera + entity probes. */
    centerCell(cellSize) {
        return {
            c: Math.floor((this.x + this.w / 2) / cellSize),
            r: Math.floor((this.y + this.h / 2) / cellSize),
        };
    }

    destroy() {
        this.display.removeChildren();
        if (this.display.parent) this.display.parent.removeChild(this.display);
    }
}
